/**
 * The venue's / host organization's CTAs for an event (owner 2026-10-29, migration 2026-10-29-1260).
 *
 * A class, table, seminar or speaking engagement has no event CTA of its own. Its Calls to Action tab shows,
 * read-only, every ACTIVE CTA of the organizations hosting it -- the union of event_organizations (active) and the
 * organizations of its venues (event_locations -> locations) -- with the same CTA ids, so a scan at the event is
 * the organization's own CTA. Order (server): at the event's venue, then organization-level, then the
 * organization's other locations; newest first within each.
 *
 * get_event_host_ctas is SECURITY DEFINER and answers only for an event the caller can see (active profile; event
 * not deleted; the field-manager scope when that role exists).
 */
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type { OrganizationCTA } from "../types/organizationCTA.types";

export type EventHostCTAScope =
  | "venue"
  | "organization"
  | "organization_location";

export interface EventHostCTA {
  cta: OrganizationCTA;
  organization: { id: UUID; name: string; address_state?: string };
  locationId: UUID | null;
  scope: EventHostCTAScope;
  sortRank: number;
}

interface EventHostCTARow {
  cta_id: UUID;
  organization_id: UUID;
  organization_name: string | null;
  organization_address_state: string | null;
  location_id: UUID | null;
  host_scope: EventHostCTAScope;
  sort_rank: number;
  cta: OrganizationCTA;
}

export async function getEventHostCTAs(eventId: UUID): Promise<EventHostCTA[]> {
  const { data, error } = await supabase.rpc("get_event_host_ctas", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return ((data ?? []) as EventHostCTARow[])
    .map((r) => ({
      cta: r.cta,
      organization: {
        id: r.organization_id,
        name: r.organization_name ?? "Organization",
        address_state: r.organization_address_state ?? undefined,
      },
      locationId: r.location_id,
      scope: r.host_scope,
      sortRank: r.sort_rank,
    }))
    .sort((a, b) => a.sortRank - b.sortRank);
}

export interface EventHostOrganization {
  id: UUID;
  name: string;
}

/**
 * The event's host organizations (same union as the server), for the empty state's "add CTAs on the organization's
 * page" link. Read with the caller's own RLS (both link tables are readable by staff).
 */
export async function getEventHostOrganizations(
  eventId: UUID,
): Promise<EventHostOrganization[]> {
  const [orgLinks, locLinks] = await Promise.all([
    supabase
      .from("event_organizations")
      .select("organization_id, status, organization:organizations(id, name)")
      .eq("event_id", eventId),
    supabase
      .from("event_locations")
      .select(
        "location:locations(organization_id, organization:organizations(id, name))",
      )
      .eq("event_id", eventId),
  ]);
  if (orgLinks.error) {
    throw orgLinks.error;
  }
  if (locLinks.error) {
    throw locLinks.error;
  }
  const byId = new Map<UUID, EventHostOrganization>();
  type OrgRef = { id: UUID; name: string } | null;
  for (const row of (orgLinks.data ?? []) as unknown as Array<{
    status: string | null;
    organization: OrgRef;
  }>) {
    if ((row.status ?? "active") === "active" && row.organization) {
      byId.set(row.organization.id, row.organization);
    }
  }
  for (const row of (locLinks.data ?? []) as unknown as Array<{
    location: { organization: OrgRef } | null;
  }>) {
    const org = row.location?.organization;
    if (org) {
      byId.set(org.id, org);
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}
